import React, { useState } from 'react';
import { X, Copy, Check, ExternalLink, Info, Bot, Zap, Wind, Code2 } from 'lucide-react';

// AI Builder options (no emojis, using Lucide icons)
const aiBuilders = [
    { 
        id: 'claude-desktop', 
        name: 'Claude Desktop', 
        IconComponent: Bot,
        description: 'Add to claude_desktop_config.json'
    },
    { 
        id: 'cursor', 
        name: 'Cursor', 
        IconComponent: Zap,
        description: 'Add to .cursor/mcp.json'
    },
    { 
        id: 'windsurf', 
        name: 'Windsurf', 
        IconComponent: Wind,
        description: 'Add to mcp_config.json'
    },
    { 
        id: 'other', 
        name: 'Other IDEs', 
        IconComponent: Code2,
        description: 'Generic MCP configuration'
    },
];

// MCP endpoint mapping
const mcpEndpoints = {
    'razorpay-mcp': 'razorpay',
    'frontend-mcp': 'frontend',
    'design-mcp': 'design',
    'supabase-auth-mcp': 'supabase-auth',
    'stripe-mcp': 'stripe',
};

export default function McpConnectionModal({ isOpen, onClose, mcp }) {
    const [selectedBuilder, setSelectedBuilder] = useState('claude-desktop');
    const [copiedSection, setCopiedSection] = useState(null);

    if (!isOpen) return null;

    const mcpBaseUrl = 'https://mcp.pluckr.ai';
    const endpoint = mcpEndpoints[mcp?.id] || 'razorpay';
    const mcpName = mcp?.title || 'Razorpay MCP';
    
    // Connection URL with placeholder
    const connectionUrl = `${mcpBaseUrl}/${endpoint}?apiKey=YOUR_API_KEY`;

    const handleCopy = (section, text) => {
        navigator.clipboard.writeText(text);
        setCopiedSection(section);
        setTimeout(() => setCopiedSection(null), 2000);
    };

    // Generate config based on selected builder
    const getConfig = () => {
        switch (selectedBuilder) {
            case 'claude-desktop':
                return JSON.stringify({
                    mcpServers: {
                        [endpoint]: {
                            url: connectionUrl,
                            transport: 'sse'
                        }
                    }
                }, null, 2);
            case 'cursor':
                return JSON.stringify({
                    mcpServers: {
                        [endpoint]: {
                            url: connectionUrl,
                            transport: 'sse'
                        }
                    }
                }, null, 2);
            case 'windsurf':
                return JSON.stringify({
                    mcpServers: {
                        [endpoint]: {
                            serverUrl: connectionUrl,
                            transport: 'sse'
                        }
                    }
                }, null, 2);
            case 'other':
                return JSON.stringify({
                    mcpServers: {
                        [endpoint]: {
                            url: connectionUrl,
                            transport: 'sse'
                        }
                    }
                }, null, 2);
            default:
                return connectionUrl;
        }
    };

    const getConfigFileName = () => {
        switch (selectedBuilder) {
            case 'claude-desktop':
                return 'claude_desktop_config.json';
            case 'cursor':
                return '.cursor/mcp.json';
            case 'windsurf':
                return 'mcp_config.json';
            case 'other':
                return 'mcp_config.json';
            default:
                return 'config.json';
        }
    };

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 transition-opacity"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div
                    className="relative w-full max-w-2xl bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-2xl overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                    style={{ animation: 'modalSlideIn 0.3s ease-out' }}
                >
                    {/* Header */}
                    <div className="relative">
                        <div
                            className="absolute top-0 left-0 right-0 h-1"
                            style={{
                                background: 'linear-gradient(90deg, #9be38f 0%, #6dd55f 50%, #4ade80 100%)'
                            }}
                        />
                        <div className="flex items-center justify-between px-6 pt-6 pb-4">
                            <div>
                                <h2
                                    className="text-xl font-bold text-[#1a1a1a] dark:text-white"
                                    style={{ fontFamily: "'Lora', serif" }}
                                >
                                    Connect {mcpName}
                                </h2>
                                <p className="text-[#5a5a5a] dark:text-[#a0a0a0] text-sm mt-1">
                                    Add this MCP server to your AI coding assistant
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/10 transition-colors text-[#5a5a5a] dark:text-white/70"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="px-6 pb-6 space-y-5">
                        
                        {/* Important Note */}
                        <div className="flex items-start gap-3 p-4 rounded-xl bg-[#fffbeb] dark:bg-[#3d3520] border border-[#fcd34d]/30">
                            <Info className="w-5 h-5 text-[#d97706] flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm text-[#92400e] dark:text-[#fcd34d] font-medium">
                                    Replace YOUR_API_KEY with your actual API key
                                </p>
                                <p className="text-xs text-[#a16207] dark:text-[#d4a855] mt-1">
                                    Go to <span className="font-semibold">Dashboard → API Keys</span> to create or copy your API key.
                                </p>
                            </div>
                        </div>

                        {/* AI Builder Selection */}
                        <div>
                            <label className="text-sm font-medium text-[#1a1a1a] dark:text-white mb-3 block">
                                Select your AI Builder
                            </label>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                {aiBuilders.map((builder) => {
                                    const IconComponent = builder.IconComponent;
                                    return (
                                        <button
                                            key={builder.id}
                                            onClick={() => setSelectedBuilder(builder.id)}
                                            className={`p-3 rounded-xl text-left transition-all border-2 ${
                                                selectedBuilder === builder.id
                                                    ? 'border-[#9be38f] bg-[#f0fdf4] dark:bg-[#1a2e1a]'
                                                    : 'border-[#e5e7eb] dark:border-white/10 hover:border-[#9be38f]/50 bg-white dark:bg-[#282929]'
                                            }`}
                                        >
                                            <IconComponent className={`w-6 h-6 mb-1 ${
                                                selectedBuilder === builder.id 
                                                    ? 'text-[#166534] dark:text-[#9be38f]' 
                                                    : 'text-[#5a5a5a] dark:text-white/70'
                                            }`} />
                                            <span className={`text-sm font-medium block ${
                                                selectedBuilder === builder.id 
                                                    ? 'text-[#166534] dark:text-[#9be38f]' 
                                                    : 'text-[#1a1a1a] dark:text-white'
                                            }`}>
                                                {builder.name}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Connection URL */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-sm font-medium text-[#1a1a1a] dark:text-white">
                                    Connection URL
                                </label>
                                <button
                                    onClick={() => handleCopy('url', connectionUrl)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                        copiedSection === 'url'
                                            ? 'bg-[#9be38f] text-[#166534]'
                                            : 'bg-[#f5f5f5] dark:bg-white/10 text-[#5a5a5a] dark:text-white/70 hover:bg-[#e5e5e5] dark:hover:bg-white/15'
                                    }`}
                                >
                                    {copiedSection === 'url' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                                    {copiedSection === 'url' ? 'Copied!' : 'Copy'}
                                </button>
                            </div>
                            <div className="p-3 rounded-xl bg-[#1a1a1a] dark:bg-[#0d0d0d] border border-[#333] overflow-x-auto">
                                <code className="text-sm text-[#9be38f] font-mono break-all">
                                    {connectionUrl}
                                </code>
                            </div>
                        </div>

                        {/* Configuration */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-sm font-medium text-[#1a1a1a] dark:text-white">
                                    Add to <span className="text-[#9be38f]">{getConfigFileName()}</span>
                                </label>
                                <button
                                    onClick={() => handleCopy('config', getConfig())}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                        copiedSection === 'config'
                                            ? 'bg-[#9be38f] text-[#166534]'
                                            : 'bg-[#f5f5f5] dark:bg-white/10 text-[#5a5a5a] dark:text-white/70 hover:bg-[#e5e5e5] dark:hover:bg-white/15'
                                    }`}
                                >
                                    {copiedSection === 'config' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                                    {copiedSection === 'config' ? 'Copied!' : 'Copy Config'}
                                </button>
                            </div>
                            <div className="p-4 rounded-xl bg-[#1a1a1a] dark:bg-[#0d0d0d] border border-[#333] overflow-x-auto max-h-48">
                                <pre className="text-sm text-[#e5e5e5] font-mono whitespace-pre">
                                    {getConfig()}
                                </pre>
                            </div>
                        </div>
                    </div>

                    {/* Animation styles */}
                    <style>{`
                        @keyframes modalSlideIn {
                            from {
                                opacity: 0;
                                transform: translateY(20px) scale(0.98);
                            }
                            to {
                                opacity: 1;
                                transform: translateY(0) scale(1);
                            }
                        }
                    `}</style>
                </div>
            </div>
        </>
    );
}
